import { AnyAction, createAsyncThunk } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import i18n from "config/i18n"
import { PUBLIC_API } from "redux/api"
import {
  AUTHENTICATION_POLLING_INTERVAL,
  AUTHENTICATION_POLLING_TIMEOUT,
  MOBILE_ID_ERRORS,
} from "config"
import PollingService from "redux/api/pollingService"
import { login } from "redux/features/auth/authSlice"
import {
  start as startVerification,
  reset as resetVerification,
} from "redux/features/verification/verificationSlice"
import { start as startPolling } from "redux/features/polling/pollingSlice"
import { AuthSessionValues, MobileIdValues } from "types"

export const createMobileIdSession = createAsyncThunk(
  "mobile-id-session/create",
  async (values: MobileIdValues) => {
    const response = await PUBLIC_API.post("/authentication/mobile_id", values)

    return response.data
  },
)

export const fetchMobileIdSession = createAsyncThunk(
  "mobile-id-session/fetch",
  async (values: AuthSessionValues) => {
    return await PUBLIC_API.get(
      `/authentication/mobile_id/${values.session_id}`,
    )
      .then((result) => {
        return result.data
      })
      .catch((error) => {
        return error.response.data
      })
  },
)

export const makeMobileIdAuthentication = (values: MobileIdValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise((resolve, reject) => {
      return dispatch(createMobileIdSession(values))
        .then(
          ({ payload }) => {
            if (!payload) {
              return reject(i18n.t("notice.sessions.mobile_id.unavailable"))
            }

            const { session_id, verification_code } = payload.authentication
            dispatch(startVerification({ code: verification_code }))

            const timerId = setInterval(async () => {
              const pollingId = PollingService.getPolling()

              if (!pollingId) {
                clearInterval(timerId)
                return reject(i18n.t("notice.sessions.mobile_id.canceled"))
              }

              const result = await dispatch(
                fetchMobileIdSession({ session_id }),
              )
              const { payload } = result

              if (payload?.authentication) {
                clearInterval(timerId)
                PollingService.resetPolling()

                const { authentication } = result.payload
                const { id, access_token, refresh_token } = authentication

                dispatch(
                  login({
                    id: id,
                    accessToken: access_token,
                    refreshToken: refresh_token,
                  }),
                )
                dispatch(resetVerification())

                resolve(authentication)
              } else if (
                payload?.error &&
                MOBILE_ID_ERRORS.includes(payload.error)
              ) {
                clearInterval(timerId)
                PollingService.resetPolling()
                dispatch(resetVerification())

                reject(
                  i18n.t(
                    `notice.sessions.mobile_id.${payload.error.toLowerCase()}`,
                  ),
                )
              }
            }, AUTHENTICATION_POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout(() => {
              clearInterval(timerId)
              PollingService.resetPolling()
            }, AUTHENTICATION_POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.sessions.mobile_id.not_found"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}
